import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './RemitoPrintView.css';
import { openPdf } from '../utils/pdfHelpers';

interface RemitoItem {
  name: string;
  description?: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  type?: string;
}

interface Remito {
  id: string;
  remitNumber: string;
  clientId: string;
  vehicleId?: string;
  clientName: string;
  companyName?: string;
  vehicleInfo?: string;
  licensePlate?: string;
  type: 'service' | 'delivery' | 'warranty' | 'internal';
  serviceType?: string;
  observations?: string;
  status: 'draft' | 'issued' | 'delivered' | 'cancelled';
  totalQuantity: number;
  estimatedValue: number;
  issuedAt?: string;
  deliveredAt?: string;
  receivedBy?: string;
  receivedByDni?: string | null;
  createdAt: string;
  createdByName: string;
  relatedBudgetId?: string;
  relatedAppointmentId?: string;
  paymentMethod?: string;
  paymentInterval?: number;
  emailSent?: boolean;
  totalContado?: number;
  /** Cobro mixto (paymentMethod 'mixto'): un renglón por medio de pago */
  payments?: Array<{ method: string; base: number; surchargePct: number; surcharge: number; amount: number }>;
  items: RemitoItem[];
}

interface CompanySettings {
  name: string;
  address: string;
  phone: string;
  whatsapp: string;
  email: string;
  cuit: string;
  instagram: string;
  weekdayHours: string;
  saturdayHours: string;
  logo?: string;
  activityStartDate?: string;
  taxStatus?: string;
}

interface RemitoPrintViewProps {
  remito: Remito;
  onClose: () => void;
  appointments?: any[];
  processedItems?: RemitoItem[]; // Items ya procesados de la vista
}

const RemitoPrintView: React.FC<RemitoPrintViewProps> = ({ remito, onClose, appointments, processedItems: itemsFromProps }) => {
  const [processedItems, setProcessedItems] = useState<RemitoItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [companySettings, setCompanySettings] = useState<CompanySettings>({
    name: 'ROCAM SRL',
    address: 'BV OROÑO 620 - SAN LORENZO, SANTA FE',
    phone: '3476409820',
    whatsapp: '3476409820',
    email: 'ventas@rocam.com.ar',
    cuit: '30-71664233-6',
    instagram: '@rocamneumaticos',
    weekdayHours: '',
    saturdayHours: '',
    activityStartDate: '16/10/2019',
    taxStatus: 'RESPONSABLE INSCRIPTO'
  });

  // Detectar si estamos en modo PDF
  const isPDFMode = window.location.pathname.includes('/remito/preview-print/');

  // Cargar datos de la empresa
  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        // Intentar primero con autenticación, luego público
        const token = localStorage.getItem('token');
        const endpoint = token ? '/api/settings/company' : '/api/settings/company/public';
        const response = await axios.get(endpoint);
        if (response.data) {
          setCompanySettings(prev => ({
            ...prev,
            name: response.data.name || prev.name,
            address: response.data.address || prev.address,
            phone: response.data.phone || prev.phone,
            whatsapp: response.data.whatsapp || prev.whatsapp,
            email: response.data.email || prev.email,
            cuit: response.data.cuit || prev.cuit,
            instagram: response.data.instagram || prev.instagram,
            weekdayHours: response.data.weekdayHours || '',
            saturdayHours: response.data.saturdayHours || '',
            logo: response.data.logo || '',
            activityStartDate: response.data.activityStartDate || prev.activityStartDate,
            taxStatus: response.data.taxStatus || prev.taxStatus
          }));
        }
      } catch (error) {
        console.log('No se pudieron cargar los datos de la empresa, usando valores por defecto');
      }
    };
    fetchCompanySettings();
  }, []);

  // Función auxiliar para formatear teléfono
  const formatPhoneForDisplay = (value: string = '') => {
    if (!value) return '';
    const digits = String(value).replace(/\D/g, '');
    if (!digits) return String(value).trim();
    let normalized = digits;
    if (normalized.startsWith('549') && normalized.length > 3) {
      normalized = normalized.slice(3);
    } else if (normalized.startsWith('54') && normalized.length > 2) {
      normalized = normalized.slice(2);
    }
    if (normalized.length > 6) {
      const areaLength = normalized.length - 6;
      return `${normalized.slice(0, areaLength)} ${normalized.slice(areaLength)}`;
    }
    return normalized;
  };

  // Función para formatear CUIT
  const formatCuit = (cuit: string = '') => {
    if (!cuit) return '';
    const digits = cuit.replace(/\D/g, '');
    if (digits.length === 11) {
      return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
    }
    return cuit;
  };

  // Función para formatear fecha
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit', 
      year: '2-digit' 
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Si recibimos items ya procesados, usarlos directamente
  useEffect(() => {
    console.log('🔍 Debug RemitoPrintView:');
    console.log('📋 remito.items:', remito.items);
    console.log('📦 itemsFromProps:', itemsFromProps);
    console.log('💰 remito.estimatedValue:', remito.estimatedValue);
    console.log('💳 remito.totalContado:', remito.totalContado);
    
    if (itemsFromProps && itemsFromProps.length > 0) {
      console.log('✅ Usando items ya procesados de la vista:', itemsFromProps);
      setProcessedItems(itemsFromProps);
    } else if (remito.items && remito.items.length > 0) {
      console.log('✅ Usando items del remito:', remito.items);
      // Procesar items del remito para asegurar que tengan todos los campos
      const processed = remito.items.map((item, index) => {
        console.log(`🔍 Procesando item ${index} del remito:`, item);
        
        const processedItem = {
          name: item.name || item.description || 'Item sin nombre',
          description: item.description || item.name || 'Sin descripción',
          quantity: Number(item.quantity) || 1,
          unitPrice: Number(item.unitPrice) || 0,
          totalPrice: Number(item.totalPrice) || (Number(item.unitPrice || 0) * Number(item.quantity || 1)),
          type: item.type || 'product'
        };
        
        console.log(`✅ Item ${index} procesado:`, processedItem);
        return processedItem;
      });
      
      setProcessedItems(processed);
    } else {
      console.log('⚠️ No hay items disponibles, usando array vacío');
      setProcessedItems([]);
    }
  }, [remito, itemsFromProps]);

  // Función para procesar items (solo como fallback, no se usa normalmente)
  const processItems = async () => {
    // Esta función ya no se usa normalmente, solo como fallback
    console.log('🔄 Usando fallback de procesamiento de items');
    setProcessedItems(remito.items || []);
  };

  const handlePrint = async () => {
    try {
      // Generar el mismo PDF del backend que se envía por WhatsApp/email
      const response = await axios.post(`/api/remitos/${remito.id}/generate-pdf`, {}, { responseType: 'blob' });
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const filename = remito.remitNumber
        ? `Remito-${remito.remitNumber}.pdf`
        : `Remito-${remito.id.slice(-6)}.pdf`;
      openPdf({ blob: pdfBlob, filename });
    } catch (error) {
      // Fallback: imprimir el HTML actual si algo falla
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        const printContent = document.querySelector('.remito-page')?.outerHTML;
        if (printContent) {
          printWindow.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <title>Remito ${remito.remitNumber}</title>
                <link rel="stylesheet" href="${window.location.origin}/src/components/RemitoPrintView.css">
                <style>
                  @media print {
                    body { margin: 0; }
                    .remito-page { 
                      width: 210mm; 
                      height: 297mm; 
                      margin: 0; 
                      padding: 15mm;
                      box-shadow: none;
                      border: none;
                    }
                  }
                </style>
              </head>
              <body>
                ${printContent}
              </body>
            </html>
          `);
          printWindow.document.close();
          printWindow.focus();
          printWindow.print();
          printWindow.close();
        }
      }
    }
  };

  const getPaymentMethodDescription = (paymentMethod: string) => {
    const descriptions: { [key: string]: string } = {
      efectivo: 'Efectivo',
      debito: 'Débito',
      transferencia: 'Transferencia',
      tarjeta_3: 'Tarjeta 3 cuotas (+15%)',
      tarjeta_6: 'Tarjeta 6 cuotas (+24%)',
      tc3: 'Tarjeta 3 cuotas (+15%)',
      tc6: 'Tarjeta 6 cuotas (+25%)',
      qr: 'QR',
      cheque: 'Cheque',
      cuenta_corriente: 'Cuenta Corriente',
      mixto: 'Pago mixto'
    };

    return descriptions[paymentMethod] || 'No especificado';
  };



  // Calcular puntos de fidelización (ejemplo: 1 punto por cada $100)
  const calculateLoyaltyPoints = (total: number) => {
    return Math.floor(total / 100);
  };

  const totalForLoyalty = (() => {
    // Si hay método de pago, usar totalContado; si no, calcular total de items
    if (remito.paymentMethod && remito.totalContado) {
      console.log('💰 Usando totalContado para fidelización:', remito.totalContado);
      return Number(remito.totalContado);
    } else {
      // Calcular total de items procesados
      const totalFromItems = processedItems.reduce((sum, item) => {
        const itemTotal = Number(item.totalPrice || 0);
        console.log(`🔍 Item ${item.name}: cantidad=${item.quantity}, precio=${item.unitPrice}, total=${itemTotal}`);
        return sum + itemTotal;
      }, 0);
      console.log('💰 Total calculado de items para fidelización:', totalFromItems);
      return totalFromItems;
    }
  })();
  
  const loyaltyPoints = calculateLoyaltyPoints(totalForLoyalty);

  return (
    <div className={`remito-print-overlay ${isPDFMode ? 'pdf-mode' : ''}`}>
      <div className="remito-print-container">
        {/* Solo mostrar header con botón cerrar (sin imprimir) */}
        {!isPDFMode && (
          <div className="remito-print-header">
            <button onClick={onClose} className="close-button">
              ✕
            </button>
          </div>
        )}
        
        <div className="remito-page">
          {/* Encabezado */}
          <div className="remito-header">
            {/* Logo de la empresa (dejar espacio) */}
            <div className="logo-section">
              <img
                src={companySettings.logo || "/logo-rocam.svg"}
                alt={companySettings.name}
                className="company-logo"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  target.nextElementSibling?.classList.remove('fallback-hidden');
                }}
              />
              <div className="company-name fallback-hidden">{companySettings.name}</div>
            </div>

            {/* Datos fiscales alineados a la derecha */}
            <div className="fiscal-data">
              <h1 className="company-title">{companySettings.name}</h1>
              <p className="fiscal-address">{companySettings.address}</p>
              <p className="fiscal-cuit">CUIT: {formatCuit(companySettings.cuit)}</p>
              {companySettings.activityStartDate && (
                <p className="fiscal-date">INICIO ACTIVIDADES: {companySettings.activityStartDate}</p>
              )}
              {companySettings.taxStatus && (
                <p className="fiscal-status">{companySettings.taxStatus}</p>
              )}
            </div>
          </div>

          {/* Datos del remito */}
          <div className="remito-data">
            <div className="remito-number">
              <strong>COMPROBANTE N°:</strong> {remito.remitNumber.replace('R', '')}
            </div>
            <div className="remito-date">
              <strong>FECHA:</strong> {formatDate(remito.createdAt)}
            </div>
          </div>

          {/* Información del cliente */}
          <div className="client-data">
            <div className="client-row">
              <strong>CLIENTE:</strong> {remito.clientName}
            </div>
            <div className="client-row">
              <strong>DNI o CUIT:</strong> {remito.clientId && remito.clientId.length > 0 && !remito.clientId.includes('-') ? remito.clientId : 'No especificado'}
            </div>
            <div className="client-row">
              <strong>DOMICILIO:</strong> {remito.companyName || 'No especificado'}
            </div>
            <div className="client-row">
              <strong>VEHÍCULO:</strong> {remito.vehicleInfo || 'No especificado'} {remito.licensePlate ? `- ${remito.licensePlate}` : ''}
            </div>
            <div className="client-row">
              <strong>FORMA DE PAGO:</strong>{' '}
              {remito.paymentMethod === 'mixto' && Array.isArray(remito.payments) && remito.payments.length
                ? `Pago mixto: ${remito.payments.map((p) => `${getPaymentMethodDescription(p.method)} $${Math.round(Number(p.amount) || 0).toLocaleString('es-AR')}`).join(' + ')}`
                : remito.paymentMethod ? getPaymentMethodDescription(remito.paymentMethod) : 'No especificado'}
            </div>
          </div>

          {/* Tabla de servicios/productos */}
          <div className="items-section">
            <table className="items-table">
              <thead>
                <tr>
                  <th className="quantity-col">Cantidad</th>
                  <th className="description-col">Descripción</th>
                  <th className="price-col">Precio Unitario</th>
                  <th className="subtotal-col">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {processedItems && processedItems.length > 0 ? (
                  processedItems.map((item, index) => (
                    <tr key={index}>
                      <td className="quantity-col">{item.quantity}</td>
                      <td className="description-col">{item.name}</td>
                      <td className="price-col">{formatCurrency(item.unitPrice)}</td>
                      <td className="subtotal-col">{formatCurrency(item.totalPrice)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="no-items">No hay servicios/productos registrados</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Total */}
          <div className="total-section">
            <div className="total-row">
              <span className="total-label">TOTAL:</span>
              <span className="total-amount">
                {(() => {
                  // Si hay método de pago, usar totalContado; si no, calcular total de items
                  if (remito.paymentMethod && remito.totalContado) {
                    console.log('💰 Mostrando totalContado en PDF:', remito.totalContado);
                    return formatCurrency(Number(remito.totalContado));
                  } else {
                    // Calcular total de items procesados
                    const totalFromItems = processedItems.reduce((sum, item) => {
                      const itemTotal = Number(item.totalPrice || 0);
                      console.log(`🔍 Item ${item.name}: total=${itemTotal}`);
                      return sum + itemTotal;
                    }, 0);
                    console.log('💰 Total calculado de items en PDF:', totalFromItems);
                    return formatCurrency(totalFromItems);
                  }
                })()}
              </span>
            </div>
          </div>

          {/* Sección de fidelización */}
          <div className="loyalty-section">
            <div className="loyalty-card">
              <div className="loyalty-icon">💳</div>
              <div className="loyalty-content">
                <p className="loyalty-text">
                  Este servicio suma: <strong>{loyaltyPoints} puntos</strong> en el programa de fidelización Rocam Beneficios.
                </p>
                <p className="loyalty-conversion">
                  (conversión: 1 punto por cada $100 de compra)
                </p>
              </div>
            </div>
          </div>

          {/* Observaciones */}
          <div className="observations-section">
            <div className="observations-header">
              <strong>Observaciones:</strong>
            </div>
            <div className="observations-content">
              {remito.status === 'cancelled' ? 'Remito cancelado' : remito.status === 'delivered' ? 'Remito entregado' : remito.status === 'issued' ? 'Remito emitido' : 'Sin observaciones'}
            </div>
          </div>

          {/* Firma de entrega */}
          <div className="signature-section">
            <div className="signature-line">
              <span className="signature-label">Firma del cliente:</span>
              <div className="signature-space">_______________________</div>
            </div>
            <div className="signature-details">
              {/* Si se cargó quién retiró (v12.6.4), sale completo */}
              <div className="signature-field">
                <span className="signature-label">Aclaración:</span>
                <div className="signature-space">{remito.receivedBy || '_______________________'}</div>
              </div>
              <div className="signature-field">
                <span className="signature-label">DNI:</span>
                <div className="signature-space">
                  {remito.receivedByDni ? String(remito.receivedByDni).replace(/\B(?=(\d{3})+(?!\d))/g, '.') : '_______________________'}
                </div>
              </div>
              <div className="signature-field">
                <span className="signature-label">Fecha:</span>
                <div className="signature-space">
                  {remito.receivedBy && remito.deliveredAt ? new Date(remito.deliveredAt).toLocaleDateString('es-AR') : '___ / ___ / _____'}
                </div>
              </div>
            </div>
          </div>

          {/* Pie del remito */}
          <div className="footer-section">
            <div className="footer-info">
              {companySettings.address && (
                <div className="footer-item">
                  <span className="footer-icon">📍</span>
                  <span>{companySettings.address}</span>
                </div>
              )}
              {companySettings.whatsapp && (
                <div className="footer-item">
                  <span className="footer-icon">📞</span>
                  <span>WhatsApp: {formatPhoneForDisplay(companySettings.whatsapp)}</span>
                </div>
              )}
              {companySettings.instagram && (
                <div className="footer-item">
                  <span className="footer-icon">📸</span>
                  <span>Instagram: {companySettings.instagram}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RemitoPrintView; 
